import { toolkitActions } from "./toolkitStore";

export type ToolkitNativeAction = {
  type: string;
} & Record<string, unknown>;

type NativeActionListener = (action: ToolkitNativeAction) => void;

const listeners = new Set<NativeActionListener>();
let isInstalled = false;
const INVALID_NATIVE_ACTION_ERROR = "Native action must be a JSON object with a string `type`.";

function parseNativeAction(rawAction: string): ToolkitNativeAction {
  const parsed = JSON.parse(rawAction);

  if (!parsed || typeof parsed !== "object") {
    throw new Error(INVALID_NATIVE_ACTION_ERROR);
  }

  const maybeAction = parsed as Record<string, unknown>;

  if (typeof maybeAction.type !== "string") {
    throw new Error(INVALID_NATIVE_ACTION_ERROR);
  }

  return maybeAction as ToolkitNativeAction;
}

function isExpectedNativeActionParseError(error: unknown) {
  return error instanceof SyntaxError || (error instanceof Error && error.message === INVALID_NATIVE_ACTION_ERROR);
}

function flushNativeActionQueue() {
  if (!listeners.size) return;

  const queuedActions = toolkitActions.drainNativeActionQueue();

  for (const rawAction of queuedActions) {
    let action: ToolkitNativeAction;

    try {
      action = parseNativeAction(rawAction);
    } catch (error) {
      if (!isExpectedNativeActionParseError(error)) {
        throw error;
      }
      toolkitActions.recordNativeActionError(rawAction, error);
      continue;
    }

    for (const listener of listeners) {
      listener(action);
    }
  }
}

export function installToolkitNativeBridge() {
  if (isInstalled) {
    flushNativeActionQueue();
    return;
  }

  isInstalled = true;

  const queuedActions = [...(global.dispatchActionsOnLoad ?? [])];
  global.dispatchActionsOnLoad = [];

  for (const queuedAction of queuedActions) {
    toolkitActions.enqueueNativeAction(queuedAction);
  }

  global.dispatchAction = (rawAction: string) => {
    toolkitActions.enqueueNativeAction(rawAction);
    flushNativeActionQueue();
  };

  flushNativeActionQueue();
}

export function subscribeNativeActions(listener: NativeActionListener) {
  listeners.add(listener);
  flushNativeActionQueue();

  return () => {
    listeners.delete(listener);
  };
}

export function resetToolkitNativeBridge() {
  listeners.clear();
  isInstalled = false;
  global.dispatchActionsOnLoad = [];
  global.dispatchAction = (rawAction: string) => {
    global.dispatchActionsOnLoad.push(rawAction);
  };
}
